import React, { useState, useContext, useEffect } from "react";
import { useMutation } from "@apollo/client";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import { REDEEM_VOUCHER } from "src/gql/mutations";
import { betMGMDismissedVar, betMGMPendingVar, currentSport } from "src/services/ApolloService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import styles from "./styles.scss";
import Tooltip from "src/components/Tooltip";
import { useVouchers } from "src/services/UserService";
import { usePeristedVar } from "src/services/UtilService";
import BetQLLogo from "src/components/BetQLLogo";
import BetMGMLogo from "public/logos/BetMGM.svg";
import AnalyticsService from "src/services/AnalyticsService";
import FocusedTextLink from "../FocusedTextLink";
import { STATE_MAP } from "../../constants";

const BetMGMReminder = () => {
  const ctx = useContext(SubscriptionContext);
  const [redeemVoucher, { loading }] = useMutation(REDEEM_VOUCHER);
  const [betMGMPending, setBetMGMPending] = usePeristedVar(betMGMPendingVar, "betMGMPending", false); // prettier-ignore
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Reset on close
  useEffect(() => {
    if (!betMGMPending) {
      setCode("");
      setErrorMessage("");
    }
  }, [betMGMPending]);

  const redeemOffer = async () => {
    try {
      const { data } = await redeemVoucher({
        variables: {
          code,
        },
      });

      const token = data.redeemVoucher.token;

      await ctx.setSubscriptionLevel(token);
      setBetMGMPending(false);

      ctx.togglePricingModal({
        source: "OfferBanner",
        deepLink: "congrats",
      });

      AnalyticsService.track("Redeem Offer BetMGM Success", {
        page: window.location.href,
        sport: currentSport()?.name,
        source: "BetMGM Banner",
        state: ctx.userLocale,
        code,
      });
    } catch (e: any) {
      setErrorMessage(e?.message ?? "We don't recognize that code");

      AnalyticsService.track("Redeem Offer BetMGM Failure", {
        page: window.location.href,
        sport: currentSport()?.name,
        source: "BetMGM Banner",
        state: ctx.userLocale,
        code,
      });
    }
  };

  return (
    <div className="betmgm-reminder">
      <div className="left">
        <span className="reminder-text">
          <span style={{ color: "#fbcc33" }}>Complete BetMGM Offer!</span> Paste verification code and get 12 free
          months of BetQL.
        </span>

        <span style={{ display: "inline-flex", marginLeft: 5 }}>
          <Tooltip title="Help" direction="bottom">
            Getting stuck?{" "}
            <a href="https://support.betql.co/hc/en-us/requests/new" target="_blank" rel="noopener">
              Contact us
            </a>{" "}
            for help.
          </Tooltip>
        </span>
      </div>
      <div style={{ height: 6, width: 16 }} />
      <div className="code-form">
        <div className="code-area">
          <input
            className={`code-input ${errorMessage ? "error" : ""}`}
            type="text"
            placeholder="Paste code here"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onFocus={() => setErrorMessage("")}
          />
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
        <div style={{ width: 10 }} />
        <FocusedTextLink className="white-button" onClick={() => !loading && redeemOffer()}>
          {loading ? "Loading..." : "Redeem"}
        </FocusedTextLink>
      </div>
      <div style={{ width: 10 }} />
      <FocusedTextLink className="close-button" onClick={() => setBetMGMPending(false)}>
        <Icon path={mdiClose} size="20px" color="white" />
      </FocusedTextLink>
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

const BetMGMBanner = () => {
  const ctx = useContext(SubscriptionContext);
  const [, setDismissedBetMGM] = usePeristedVar(betMGMDismissedVar, "betMGMDismissed", false); // prettier-ignore
  const onClaimOffer = () => {
    ctx.togglePricingModal({
      source: "OfferBanner",
      deepLink: "offerSteps",
    });
  };

  return (
    <div className="betmgm-banner">
      <span className="offer-logos">
        <BetQLLogo color="white" style={{ height: 18 }} />
        <span style={{ width: 10 }} />
        {/* prettier-ignore */}
        <span style={{ width: 2, height: 20, background: "white", borderRadius: 10 }} />
        <span style={{ width: 10 }} />
        <img style={{ height: 24 }} src={BetMGMLogo} alt={"BetQL"} />
      </span>
      <span style={{ width: 10 }} />

      <div className="offer-text">
        <span style={{ color: "#fbcc33" }}>Deposit $10 on BetMGM</span>
        &nbsp;and get 12 free months of BetQL!
      </div>

      <span style={{ width: 16, height: 6 }} />

      <FocusedTextLink className="white-button" onClick={onClaimOffer}>
        Claim Offer
      </FocusedTextLink>

      <div className="close-button" onClick={() => setDismissedBetMGM(true)}>
        <Icon path={mdiClose} size="20px" color="white" />
      </div>
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

const OfferBanner = () => {
  const ctx = useContext(SubscriptionContext);
  const [betMGMPending] = usePeristedVar(betMGMPendingVar, "betMGMPending", false); // prettier-ignore
  const [dismissedBetMGM] = usePeristedVar(betMGMDismissedVar, "betMGMDismissed", false); // prettier-ignore
  const vouchers = useVouchers();
  const redeemed = vouchers.indexOf("BetMGM") > -1;

  if (!STATE_MAP[ctx.userLocale]?.legal) {
    return null;
  }

  const showOfferBanner =
    !ctx.subscriptionLevel && // no sub
    !redeemed && // not redeemed
    !dismissedBetMGM; // not dismissed

  // Outer span helps hint the SSR there's nothing
  return <span>{betMGMPending ? <BetMGMReminder /> : showOfferBanner ? <BetMGMBanner /> : null}</span>;
};

export default OfferBanner;
export * from "./MgmBanner";
